/**
 * Per-song auto-scroll preferences: the speed level in constant mode, the pace multiplier in smart
 * mode. Kept in localStorage rather than on the account because both depend on the screen the song
 * is being read from — a phone and a tablet want different numbers for the same song.
 *
 * There is no cap on how many songs are remembered. There used to be a 150-song LRU, which cost a
 * player their calibration on a song they hadn't opened in a while — and 150 entries is 12KB, a
 * quarter of one percent of the storage budget. Entries are dropped only if localStorage actually
 * fills up (see storage.js), which for this data would take tens of thousands of songs.
 *
 * These live under their own localStorage key, NOT in the shared `settings` blob. getSetting parses
 * that blob on every call and ViewSongPart calls it six times per part per render, so a preferences
 * map growing inside it would put a JSON.parse of every song you have ever played into the render
 * path of every song you play. Out here it is parsed when the auto-scroll controls ask for it.
 */

import { SCROLL_PREFERENCES_KEY, readItem, writeItem } from './storage';
import { getSetting, setSetting, settingKeys } from './settings';

// lastAccessed only has to be good enough to order entries for eviction, so a read refreshes it at
// most once a day. Touching it on every read would mean a write every time a song is opened.
const ACCESS_REFRESH_MS = 24 * 60 * 60 * 1000;

let migrated = false;

/**
 * Moves preferences out of the shared settings blob, once, on first use.
 * Anyone upgrading keeps every song they had; the old copy is cleared so the blob shrinks back.
 */
const migrateFromSettings = () => {
  if (migrated) return;
  migrated = true;

  try {
    const legacy = getSetting(settingKeys.scrollSpeedPreferences);
    if (!legacy || Object.keys(legacy).length === 0) return;

    const existing = JSON.parse(readItem(SCROLL_PREFERENCES_KEY) || '{}');
    // Anything already written under the new key is newer, so it wins
    writeItem(SCROLL_PREFERENCES_KEY, JSON.stringify({ ...legacy, ...existing }));
    setSetting(settingKeys.scrollSpeedPreferences, undefined);
  } catch (error) {
    // A failed migration just means the old preferences stay where they are
  }
};

/**
 * Get all scroll speed preferences
 * @returns {Object} Object with songId as keys and {speed, pace, lastAccessed} as values
 */
export const getAllScrollSpeedPreferences = () => {
  migrateFromSettings();

  try {
    return JSON.parse(readItem(SCROLL_PREFERENCES_KEY) || '{}') || {};
  } catch (error) {
    return {};
  }
};

const savePreferences = (preferences) => writeItem(SCROLL_PREFERENCES_KEY, JSON.stringify(preferences));

/** How many songs have a remembered preference — for the storage readout in Settings. */
export const countScrollSpeedPreferences = () => Object.keys(getAllScrollSpeedPreferences()).length;

/**
 * Marks a song as recently used, but only when the stored timestamp has gone stale, so reading a
 * preference doesn't write on every song change.
 */
const touch = (songId, preferences, preference) => {
  if (Date.now() - (preference.lastAccessed || 0) < ACCESS_REFRESH_MS) return;

  try {
    savePreferences({ ...preferences, [songId]: { ...preference, lastAccessed: Date.now() } });
  } catch (error) {
    // Silently fail on access tracking - not critical
  }
};

/**
 * Get the saved scroll speed for a specific song
 * @param {string} songId - The song ID
 * @returns {number|null} The saved speed or null if no preference exists
 */
export const getScrollSpeedForSong = (songId) => {
  if (!songId) return null;

  const preferences = getAllScrollSpeedPreferences();
  const preference = preferences[songId];
  if (!preference) return null;

  touch(songId, preferences, preference);

  return preference.speed ?? null;
};

/**
 * Get the saved smart-scroll pace multiplier for a song
 * @param {string} songId - The song ID
 * @returns {number|null} The saved pace or null if no preference exists
 */
export const getScrollPaceForSong = (songId) => {
  if (!songId) return null;

  const preferences = getAllScrollSpeedPreferences();
  const preference = preferences[songId];
  if (!preference) return null;

  touch(songId, preferences, preference);

  return preference.pace ?? null;
};

/**
 * Save one field of a song's preference, keeping the other.
 * @private
 */
const savePreferenceField = (songId, field, value) => {
  if (!songId || value === null || value === undefined) return;

  try {
    const preferences = getAllScrollSpeedPreferences();

    savePreferences({
      ...preferences,
      [songId]: { ...preferences[songId], [field]: value, lastAccessed: Date.now() },
    });
  } catch (error) {}
};

/**
 * Save scroll speed preference for a song
 * @param {string} songId - The song ID
 * @param {number} speed - The scroll speed to save
 */
export const saveScrollSpeedForSong = (songId, speed) => savePreferenceField(songId, 'speed', speed);

/**
 * Save the smart-scroll pace multiplier for a song.
 * Shares a record with the speed preference, so a song keeps both.
 * @param {string} songId - The song ID
 * @param {number} pace - The pace multiplier to save
 */
export const saveScrollPaceForSong = (songId, pace) => savePreferenceField(songId, 'pace', pace);

/**
 * Remove scroll speed preference for a song
 * @param {string} songId - The song ID
 */
export const removeScrollSpeedForSong = (songId) => {
  if (!songId) return;

  try {
    const preferences = getAllScrollSpeedPreferences();
    delete preferences[songId];
    savePreferences(preferences);
  } catch (error) {}
};

/**
 * Clear all scroll speed preferences
 */
export const clearAllScrollSpeedPreferences = () => {
  try {
    savePreferences({});
  } catch (error) {}
};
